import { useRef, useState } from "react";
import { Loader2, X, Plus, Users, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { imageUrl } from "@/lib/api";

const MAX_REFS = 10;

export default function ReferenceManager({ references = [], onAdd, onRemove, uploading, idPrefix = "" }) {
  const fileRef = useRef(null);
  const [refType, setRefType] = useState("character");
  const [refName, setRefName] = useState("");

  const handleFile = (file) => {
    if (!file) return;
    onAdd(file, refType === "character" ? refName.trim() : "Style", refType);
    setRefName("");
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm text-[#9ca3af]">
        Reference images — style &amp; characters ({references.length}/{MAX_REFS})
      </Label>
      <p className="text-xs text-[#9ca3af] leading-relaxed">
        Upload a <span className="text-[#00e5ff]">Style</span> image to match the look, and named
        {" "}<span className="text-[#00e5ff]">Character</span> images to keep people consistent across every scene.
      </p>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        data-testid={`${idPrefix}reference-file-input`}
        onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }}
      />

      <div className="rounded-md border border-[#272a35] bg-[#0e1015] p-3 space-y-3">
        <div className="flex gap-2">
          <button
            type="button"
            data-testid={`${idPrefix}ref-type-character`}
            onClick={() => setRefType("character")}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
              refType === "character" ? "border-[#00e5ff] bg-[#00e5ff]/10 text-[#00e5ff]" : "border-[#272a35] text-[#9ca3af] hover:text-[#f3f4f6]"
            }`}
          >
            <Users className="h-3.5 w-3.5" /> Character
          </button>
          <button
            type="button"
            data-testid={`${idPrefix}ref-type-style`}
            onClick={() => setRefType("style")}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
              refType === "style" ? "border-[#00e5ff] bg-[#00e5ff]/10 text-[#00e5ff]" : "border-[#272a35] text-[#9ca3af] hover:text-[#f3f4f6]"
            }`}
          >
            <Palette className="h-3.5 w-3.5" /> Style
          </button>
        </div>
        {refType === "character" && (
          <Input
            data-testid={`${idPrefix}ref-name-input`}
            value={refName}
            onChange={(e) => setRefName(e.target.value)}
            placeholder="Character name (e.g. Jake the stuntman)"
            className="bg-[#0b0c10] border-[#272a35] text-[#f3f4f6] focus-visible:ring-[#00e5ff] focus-visible:ring-offset-0"
          />
        )}
        <Button
          data-testid={`${idPrefix}add-reference-btn`}
          onClick={() => fileRef.current?.click()}
          disabled={uploading || references.length >= MAX_REFS || (refType === "character" && !refName.trim())}
          variant="outline"
          className="w-full rounded-md border-[#272a35] bg-transparent text-[#f3f4f6] hover:bg-[#1d2028] hover:text-[#f3f4f6]"
        >
          {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4 text-[#00e5ff]" />}
          {uploading ? "Uploading…" : "Add reference image"}
        </Button>
      </div>

      {references.length > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid={`${idPrefix}reference-grid`}>
          {references.map((r) => (
            <div key={r.image_id} className="group relative overflow-hidden rounded-md border border-[#272a35] bg-[#0e1015]" data-testid={`${idPrefix}reference-${r.image_id}`}>
              <div className="aspect-square">
                <img src={imageUrl(r.image_id)} alt={r.label} className="h-full w-full object-cover" />
              </div>
              <div className="absolute left-1 top-1 flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium">
                {r.type === "style" ? <Palette className="h-2.5 w-2.5 text-[#00e5ff]" /> : <Users className="h-2.5 w-2.5 text-[#00e5ff]" />}
                <span className="max-w-[70px] truncate">{r.type === "style" ? "Style" : r.label || "Character"}</span>
              </div>
              <button
                data-testid={`${idPrefix}remove-reference-${r.image_id}`}
                onClick={() => onRemove(r.image_id)}
                className="absolute right-1 top-1 rounded bg-black/70 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:text-red-400"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
